import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { Sparkles, Copy, ExternalLink, Printer } from "lucide-react";
import { toast } from "sonner";
import { useApi, errMsg, fmtDate } from "@/lib/api";
import { useSession } from "@/context/Session";
import { t } from "@/i18n";

function TopQuestions({ api }) {
  const nav = useNavigate();
  const [days, setDays] = useState(7);
  const [groups, setGroups] = useState(null);
  const [ai, setAi] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => { setAi(false); api.get("/insights/top-questions", { params: { days } }).then((r) => setGroups(r.data)); }, [api, days]);
  const group = async () => {
    setBusy(true);
    try { const { data } = await api.post("/insights/topics", { days }); setGroups(data); setAi(true); } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  const add = (title) => nav(`/capture?audience=customer,team&title=${encodeURIComponent(title)}`);
  return (
    <div className="card stack-sm" data-testid="top-questions">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="mono-label">{t("Top questions")}</p>
        <div className="flex gap-2">
          {[7, 30].map((d) => <button key={d} className={`nav-link ${days === d ? "active" : ""}`} onClick={() => setDays(d)} data-testid={`top-days-${d}`}>{t("{n} days", { n: d })}</button>)}
          <button className="btn btn-ghost btn-sm" disabled={busy} onClick={group} data-testid="top-group-ai"><Sparkles size={14} /> {t(busy ? "Grouping…" : "Group similar with AI")}</button>
        </div>
      </div>
      {groups === null ? <p className="faint text-sm">{t("Loading…")}</p> : !groups.length ? <p className="faint text-sm" data-testid="top-empty">{t("No customer questions yet.")}</p> : (
        <div className="divide-y divide-[var(--border-color)]">
          {groups.map((g, i) => (
            <div key={i} className="py-3 flex items-start justify-between gap-3" data-testid={`top-group-${i}`}>
              <div className="min-w-0">
                <p className="font-semibold">{ai ? g.topic : g.examples[0]}</p>
                <p className="text-xs faint">{t("Asked {n}×", { n: g.count })} · {t("{a} answered · {b} not", { a: g.answered, b: g.unanswered })}</p>
                <p className="text-xs muted truncate">{g.examples.join(" · ")}</p>
              </div>
              <button className="btn btn-sm btn-ghost shrink-0" onClick={() => add(ai ? g.suggested_title || g.topic : g.examples[0])} data-testid={`top-add-${i}`}>{t("Add answer")}</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function AnswerLog({ api }) {
  const [rows, setRows] = useState(null);
  useEffect(() => { api.get("/customer/messages").then((r) => setRows(r.data)); }, [api]);
  return (
    <div className="card stack-sm" data-testid="answer-log">
      <p className="mono-label">{t("Answer log")}</p>
      {!rows ? <p className="faint text-sm">{t("Loading…")}</p> : !rows.length ? <p className="faint text-sm">{t("No customer questions yet.")}</p> : (
        <div className="divide-y divide-[var(--border-color)] max-h-[480px] overflow-y-auto">
          {rows.map((q) => (
            <div key={q.id} className="py-3 stack-sm" data-testid={`log-row-${q.id}`}>
              <p className="text-xs faint">{t("Web")} · {fmtDate(q.created_at)} · <span className={`chip ${q.coverage === "answered" ? "chip-ok" : "chip-warn"}`}>{t(q.coverage)}</span></p>
              <p className="font-medium">{q.question}</p>
              <p className="text-sm muted">{q.answer}</p>
              {q.owner_reply && <p className="text-sm"><b className="text-[var(--status-ok)]">{t("Owner")}:</b> {q.owner_reply}</p>}
              {!!q.sources.length && <p className="text-xs faint">{t("Sources")}: {q.sources.join(", ")}</p>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Channels({ api }) {
  const [s, setS] = useState(null);
  const { me } = useSession();
  useEffect(() => { api.get("/customer/settings").then((r) => setS(r.data)); }, [api]);
  if (!s) return null;
  const url = `${window.location.origin}/c/${me.org.slug}`;
  const copy = (v) => { navigator.clipboard.writeText(v); toast.success(t("Copied")); };
  const save = async () => {
    try { const { data } = await api.put("/customer/settings", s); setS({ ...s, ...data }); toast.success(t("Saved")); } catch (e) { toast.error(errMsg(e)); }
  };
  const field = (k, label, props = {}) => (
    <label className="stack-sm text-sm"><span className="mono-label">{t(label)}</span>
      <input className="field" value={s[k] || ""} disabled={!s.can_edit} onChange={(e) => setS({ ...s, [k]: e.target.value })} data-testid={`cs-${k}`} {...props} /></label>
  );
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div className="card stack-sm items-start" data-testid="customer-qr-card">
        <p className="mono-label">{t("Customer chat link")}</p>
        <div className="bg-white p-3 rounded-xl" id="customer-qr"><QRCodeSVG value={url} size={168} data-testid="customer-qr" /></div>
        <code className="text-xs break-all" data-testid="customer-chat-url">{url}</code>
        <div className="flex gap-2">
          <button className="btn btn-ghost btn-sm" onClick={() => copy(url)} data-testid="customer-url-copy"><Copy size={14} /> {t("Copy")}</button>
          <a className="btn btn-ghost btn-sm" href={`/c/${me.org.slug}`} target="_blank" rel="noreferrer" data-testid="customer-url-open"><ExternalLink size={14} /> {t("Open")}</a>
          <button className="btn btn-ghost btn-sm" onClick={() => window.print()} data-testid="customer-qr-print"><Printer size={14} /> {t("Print")}</button>
        </div>
      </div>
      <div className="card stack-sm md:col-span-2" data-testid="customer-settings-form">
        <p className="mono-label">{t("Settings")}{!s.can_edit && ` · ${t("view only")}`}</p>
        <div className="grid md:grid-cols-2 gap-3">
          {field("business_name", "Business name")}
          {field("greeting", "Greeting")}
          {field("fallback_message", "Fallback message")}
          <label className="stack-sm text-sm"><span className="mono-label">{t("Tone")}</span>
            <select className="field" value={s.tone} disabled={!s.can_edit} onChange={(e) => setS({ ...s, tone: e.target.value })} data-testid="cs-tone">
              {["warm", "concise", "professional"].map((v) => <option key={v} value={v}>{t(v)}</option>)}
            </select></label>
        </div>
        <div className="flex flex-wrap gap-4 text-sm">
          {[["web_enabled", "Web chat on"]].map(([k, l]) => (
            <label key={k} className="flex items-center gap-2"><input type="checkbox" checked={!!s[k]} disabled={!s.can_edit} onChange={(e) => setS({ ...s, [k]: e.target.checked })} data-testid={`cs-${k}`} /> {t(l)}</label>
          ))}
        </div>
        {s.can_edit && <button className="btn btn-primary btn-sm self-start" onClick={save} data-testid="cs-save">{t("Save")}</button>}
      </div>
    </div>
  );
}

export default function Customers() {
  const api = useApi();
  return (
    <div className="page stack" data-testid="customers-page">
      <h1 className="text-3xl md:text-4xl font-bold tracking-tight">{t("Customers")}</h1>
      <TopQuestions api={api} />
      <Channels api={api} />
      <AnswerLog api={api} />
    </div>
  );
}
